import { type TranscriptCue } from '@bynder-studio/render-core';

/**
 * Parse an SRT timecode string (HH:MM:SS,mmm) into total seconds.
 */
function parseTimecode(timecode: string): number {
    const [time, ms] = timecode.trim().split(',');
    const [hours, minutes, seconds] = time.split(':').map(Number);

    return hours * 3600 + minutes * 60 + seconds + Number(ms) / 1000;
}

/**
 * Convert seconds to a frame number at a given frame rate.
 */
function secondsToFrame(seconds: number, frameRate: number): number {
    return Math.round(seconds * frameRate);
}

/**
 * Parse an SRT file content string into TranscriptCue[].
 *
 * SRT format:
 *   1
 *   00:00:01,000 --> 00:00:04,000
 *   Hello, welcome to our video!
 *
 *   2
 *   00:00:04,000 --> 00:00:08,000
 *   This is a transcript demo.
 */
export function parseSrt(srtContent: string, frameRate: number): TranscriptCue[] {
    const cues: TranscriptCue[] = [];
    // Split on double newline (handles \r\n and \n)
    const blocks = srtContent.trim().replace(/\r\n/g, '\n').split(/\n\n+/);

    blocks.forEach((block) => {
        const lines = block.split('\n');

        if (lines.length < 3) {
            return;
        }

        const index = lines[0].trim();
        const timecodeMatch = lines[1].match(/(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/);

        if (!timecodeMatch) {
            return;
        }

        const startSeconds = parseTimecode(timecodeMatch[1]);
        const endSeconds = parseTimecode(timecodeMatch[2]);
        const text = lines.slice(2).join('\n').trim();

        cues.push({
            id: `srt-cue-${index}`,
            startFrame: secondsToFrame(startSeconds, frameRate),
            endFrame: secondsToFrame(endSeconds, frameRate),
            text,
        });
    });

    return cues;
}
